import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {IFC192Index, IFC48Index, IFC96Index, IFCPlateVariant} from '../../constants/dataEnum';

// region accumulator
const CenterRound = styled.div`
  border-radius: 100%;
  background: white;
  width: 40%;
  height: 20%;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  border: solid 1px grey;
  background: #d9d9d9;
  ${(prop) => prop.style}

  &:after {
    content: '';
    position: absolute;
    height: 80%;
    width: 80%;
    border: solid 1px grey;
    border-radius: 5px;
  }
  &:before {
    content: '';
    position: absolute;
    height: 60%;
    width: 60%;
    border: solid 1px grey;
    border-radius: 5px;
  }
`;
// eslint-disable-next-line react/prop-types
function Accumulator({wrapperStyle}) {
  return (
    <Wrapper style={wrapperStyle}>
      <CenterRound />
    </Wrapper>
  );
}
// end region accumulator

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 6fr 12fr 6fr 1fr;
  gap: 2px;
`;

const Section = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: ${(prop) => prop.column};
  grid-template-rows: ${(prop) => prop.row};
  grid-template-areas: 'left middle right';
  grid-template-areas: ${(prop) =>
    prop.area === 'left middle right'
      ? 'left middle right'
      : '"well1 chamber well2" "accumulator1 chamber accumulator2" "well3 chamber well4"'};
  gap: ${(prop) => (prop.area === 'left middle right' ? '2px' : '4px')};
`;

const TroughContainer = styled.div`
  width: 80%;
  height: 85%;
  border: 0.8px solid gray;
  border-radius: 3px;
  align-self: center;
  margin-left: ${(prop) => prop.marginLeft || '0px'};
`;

const WellContainer = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: ${(prop) => `repeat(${prop.row}, 1fr)`};
  grid-template-rows: ${(prop) => `repeat(${prop.column}, 1fr)`};
  gap: 2px;
`;

const PlateBorder = styled.div`
  width: 100%;
  height: 100%;
  display: block;
`;

const Chamber = styled.div`
  width: 90%;
  height: 90%;
  background: #d9d9d9;
  margin: 5%;
`;

const ChamberWrapper = styled.div`
  width: 90%;
  height: 90%;
  border: solid 1px #d9d9d9;
  margin: 5%;
`;

const IFC192 = [
  // top row
  {
    gridTemplate: {row: '1fr', column: '1fr 16fr 1fr', area: 'left middle right'},
    wellSection: [
      {
        type: 'well',
        index: IFC192Index.INDEX_3,
        style: {gridArea: 'middle', paddingBottom: '1%'},
      },
    ],
  },
  // top middle row
  {
    gridTemplate: {row: '1fr', column: '1fr 16fr 1fr', area: 'left middle right'},
    wellSection: [
      {type: 'well', index: IFC192Index.INDEX_10, style: {gridArea: 'left'}},
      {type: 'well', index: IFC192Index.INDEX_1, style: {gridArea: 'middle'}},
      {type: 'well', index: IFC192Index.INDEX_9, style: {gridArea: 'right'}},
    ],
  },
  // middle row
  {
    gridTemplate: {
      row: '3fr 6fr 3fr',
      column: '3fr 12fr 3fr',
      area: 'well chamber well',
    },
    wellSection: [
      {type: 'well', index: IFC192Index.INDEX_6, style: {gridArea: 'well1'}},
      {type: 'well', index: IFC192Index.INDEX_5, style: {gridArea: 'well2'}},
      {type: 'well', index: IFC192Index.INDEX_8, style: {gridArea: 'well3'}},
      {type: 'well', index: IFC192Index.INDEX_7, style: {gridArea: 'well4'}},
      {type: 'chamber', style: {gridArea: 'chamber'}},
      {type: 'accumulator', style: {gridArea: 'accumulator1'}},
      {type: 'accumulator', style: {gridArea: 'accumulator2'}},
    ],
  },
  // bottom middle row
  {
    gridTemplate: {row: '1fr', column: '1fr 16fr 1fr', area: 'left middle right'},
    wellSection: [
      {type: 'well', index: IFC192Index.INDEX_12, style: {gridArea: 'left'}},
      {type: 'well', index: IFC192Index.INDEX_2, style: {gridArea: 'middle'}},
      {type: 'well', index: IFC192Index.INDEX_11, style: {gridArea: 'right'}},
    ],
  },
  // bottom row
  {
    gridTemplate: {row: '1fr', column: '1fr 16fr 1fr', area: 'left middle right'},
    wellSection: [{type: 'well', index: IFC192Index.INDEX_4, style: {gridArea: 'middle', paddingTop: '1%'}}],
  },
];
const IFC96 = [
  // top row
  {
    plateBorder: true,
    style: {borderRadius: '80px 80px 0px 0px'},
  },
  // top middle row
  {
    gridTemplate: {row: '1fr', column: '1fr 16fr 1fr', area: 'left middle right'},
    wellSection: [{type: 'well', index: IFC96Index.INDEX_1, style: {gridArea: 'middle'}}],
  },
  // middle row
  {
    gridTemplate: {
      row: '1fr',
      column: '3fr 12fr 3fr',
      area: 'well chamber well',
    },
    wellSection: [
      {type: 'chamber', style: {gridArea: 'chamber'}},
      {
        type: 'accumulator',
        style: {gridArea: 'accumulator3'},
        wrapperStyle: {height: '50%', margin: 'auto 0px'},
      },
      {
        type: 'accumulator',
        style: {gridArea: 'accumulator6'},
        wrapperStyle: {height: '50%', margin: 'auto 0px'},
      },
    ],
  },
  // bottom middle row
  {
    gridTemplate: {row: '1fr', column: '1fr 16fr 1fr', area: 'left middle right'},
    wellSection: [{type: 'well', index: IFC96Index.INDEX_2, style: {gridArea: 'middle'}}],
  },
  // bottom row
  {
    plateBorder: true,
    style: {borderRadius: '0px 0px 80px 80px'},
  },
];

const IFC48 = [
  // top row
  {
    plateBorder: true,
    style: {borderRadius: '5px 30px 5px 5px'},
  },
  // top middle row
  {
    gridTemplate: {row: '1fr', column: '1fr 16fr 1fr', area: 'left middle right'},
    wellSection: [{type: 'well', index: IFC48Index.INDEX_1, style: {gridArea: 'middle'}}],
  },
  // middle row
  {
    gridTemplate: {
      row: '1fr',
      column: '3fr 12fr 3fr',
      area: 'well chamber well',
    },
    wellSection: [{type: 'chamber', style: {gridArea: 'chamber'}}],
  },
  // bottom middle row
  {
    gridTemplate: {row: '1fr', column: '1fr 16fr 1fr', area: 'left middle right'},
    wellSection: [{type: 'well', index: IFC96Index.INDEX_2, style: {gridArea: 'middle'}}],
  },
  // bottom row
  {
    plateBorder: true,
    style: {borderRadius: '5px 30px 5px 5px'},
  },
];
// eslint-disable-next-line react/prop-types
function IFCLabware({labwareIndexes, generateWell, IFCvariant, zoom}) {
  const generateWellSection = () => {
    const sections = [];
    if (IFCvariant === IFCPlateVariant.IFC96) {
      IFC96.forEach((section, index) => {
        if (section.plateBorder) {
          sections.push(
            <PlateBorder
              // eslint-disable-next-line react/no-array-index-key
              key={`plateBorder-${index}`}
              style={section.style}
            />,
          );
        } else {
          const {wellSection, gridTemplate} = section;

          const sectionItem = [];

          wellSection.forEach((wellSect, idx) => {
            if (wellSect.type === 'well') {
              const column = labwareIndexes[wellSect.index]?.colCount;
              const row = labwareIndexes[wellSect.index]?.rowCount;

              sectionItem.push(
                <>
                  <TroughContainer />
                  <WellContainer
                    key={wellSect.index}
                    row={row}
                    column={column}
                    style={{
                      ...wellSect.style,
                      border: '0.8px solid gray',
                      borderRadius: '4px',
                      padding: '2px',
                    }}
                  >
                    {generateWell(column, row, wellSect.index)}
                  </WellContainer>
                  <TroughContainer style={{marginLeft: 'auto'}} />
                </>,
              );
            }

            if (wellSect.type === 'chamber') {
              sectionItem.push(
                // eslint-disable-next-line react/no-array-index-key
                <ChamberWrapper style={wellSect.style}>
                  <Chamber
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${wellSect.type}-${idx}`}
                  />
                </ChamberWrapper>,
              );
            }

            if (wellSect.type === 'accumulator') {
              sectionItem.push(
                <Accumulator
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${wellSect.type}-${idx}`}
                  style={wellSect.style}
                  wrapperStyle={wellSect.wrapperStyle}
                />,
              );
            }
          });

          sections.push(
            <Section
              // eslint-disable-next-line react/no-array-index-key
              key={`section-${index}`}
              column={gridTemplate.column}
              row={gridTemplate.row}
              area={gridTemplate.area}
            >
              {sectionItem}
            </Section>,
          );
        }
      });
    } else if (IFCvariant === IFCPlateVariant.IFC48) {
      IFC48.forEach((section, index) => {
        if (section.plateBorder) {
          sections.push(
            <img
              src="/svg/labwares/IFC-48-border.svg"
              alt="IFC48Border"
              width="100%"
              height="100%"
              draggable={false}
            />,
          );
        } else {
          const {wellSection, gridTemplate} = section;

          const sectionItem = [];

          wellSection.forEach((wellSect, idx) => {
            if (wellSect.type === 'well') {
              const column = labwareIndexes[wellSect.index]?.colCount;
              const row = labwareIndexes[wellSect.index]?.rowCount;

              sectionItem.push(
                <>
                  <TroughContainer />
                  <WellContainer
                    key={wellSect.index}
                    row={row}
                    column={column}
                    style={{
                      ...wellSect.style,
                      border: '0.8px solid gray',
                      borderRadius: '4px',
                      padding: '3px 2px',
                      height: zoom ? '85%' : '90%',
                      margin: 'auto',
                    }}
                  >
                    {generateWell(column, row, wellSect.index)}
                  </WellContainer>
                  <TroughContainer style={{marginLeft: 'auto'}} />
                </>,
              );
            }

            if (wellSect.type === 'chamber') {
              sectionItem.push(
                <ChamberWrapper
                  style={{
                    ...wellSect.style,
                    height: 'calc(90% + 10px)',
                    marginTop: '0%',
                  }}
                >
                  <Chamber
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${wellSect.type}-${idx}`}
                    style={{borderRadius: '5px', height: '90%', width: '90%', margin: '5%'}}
                  />
                </ChamberWrapper>,
              );
            }

            if (wellSect.type === 'accumulator') {
              sectionItem.push(
                <Accumulator
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${wellSect.type}-${idx}`}
                  style={wellSect.style}
                  wrapperStyle={wellSect.wrapperStyle}
                />,
              );
            }
          });

          sections.push(
            <Section
              // eslint-disable-next-line react/no-array-index-key
              key={`section-${index}`}
              column={gridTemplate.column}
              row={gridTemplate.row}
              area={gridTemplate.area}
            >
              {sectionItem}
            </Section>,
          );
        }
      });
    } else {
      IFC192.forEach((section, index) => {
        const {wellSection, gridTemplate} = section;

        const sectionItem = [];

        wellSection.forEach((wellSect, idx) => {
          if (wellSect.type === 'well') {
            const column = labwareIndexes[wellSect.index]?.colCount;
            const row = labwareIndexes[wellSect.index]?.rowCount;

            sectionItem.push(
              <WellContainer key={wellSect.index} row={row} column={column} style={wellSect.style}>
                {generateWell(column, row, wellSect.index)}
              </WellContainer>,
            );
          }

          if (wellSect.type === 'chamber') {
            sectionItem.push(
              // eslint-disable-next-line react/no-array-index-key
              <Chamber key={`${wellSect.type}-${idx}`} style={wellSect.style} />,
            );
          }

          if (wellSect.type === 'accumulator') {
            sectionItem.push(
              // eslint-disable-next-line react/no-array-index-key
              <Accumulator key={`${wellSect.type}-${idx}`} style={wellSect.style} />,
            );
          }
        });

        sections.push(
          <Section
            // eslint-disable-next-line react/no-array-index-key
            key={`section-${index}`}
            column={gridTemplate.column}
            row={gridTemplate.row}
            area={gridTemplate.area}
          >
            {sectionItem}
          </Section>,
        );
      });
    }
    return sections;
  };

  return (
    <Container style={IFCvariant === IFCPlateVariant.IFC48 ? {gridTemplateRows: '3fr 3fr 8fr 3fr 3fr'} : {}}>
      {generateWellSection()}
    </Container>
  );
}

IFCLabware.propTypes = {
  generateWell: PropTypes.func,
  labwareIndexes: PropTypes.object,
  IFCvariant: PropTypes.number,
  zoom: PropTypes.bool,
};

IFCLabware.defaultProps = {
  generateWell: () => {},
  labwareIndexes: {},
  IFCvariant: IFCPlateVariant.IFC96,
  zoom: false,
};

export default IFCLabware;
